import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { WhatsAppWidget } from "@/components/WhatsAppWidget";
import { useCms } from "@/lib/cms";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Heart, Search, User, Menu, X, Instagram, Youtube, Mail, ChevronRight, ChevronDown, ArrowRight, Facebook } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useSiteSettings, telHref } from "@/lib/site";

/** Scrolling (right-to-left) strip above the header. Text is managed by Super Admin → Home Page → Announcement Bar. */
export function AnnouncementBar() {
  const { data } = useQuery({
    queryKey: ["home-content"],
    queryFn: () => api.get("/home/content").then(r => r.data),
    staleTime: 60 * 1000,
  });
  const ann = data?.announcement;
  if (!ann) return <div className="bg-[#0E4D34] h-[34px]" data-testid="top-strip"/>;
  const items = (ann.items || []).filter(Boolean);
  if (!ann.enabled || items.length === 0) return null;

  // One "half" repeats the messages enough to be wider than any screen; two halves make a seamless loop.
  const repeat = Math.max(2, Math.ceil(8 / items.length));
  const half = Array.from({ length: repeat }).flatMap(() => items);
  const Half = ({ hidden }) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden || undefined}>
      {half.map((t, i) => (
        <span key={i} className="flex items-center whitespace-nowrap">
          <span className="px-6">{t}</span>
          <span className="text-[#D4AF37]">✦</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="announcement-bar bg-[#0E4D34] text-white text-[11px] tracking-[0.25em] uppercase py-2 overflow-hidden" data-testid="top-strip">
      <div className="marquee-track flex w-max" style={{ animationDuration: `${Math.max(8, Number(ann.speed) || 30) * (repeat / 2)}s` }}>
        <Half/>
        <Half hidden/>
      </div>
    </div>
  );
}

/** Categories shown in the Shop menu: those ticked "Show in Shop menu" (Admin → Categories), in their order.
 *  If none are ticked yet, the main categories are used. */
export function menuCategories(cats) {
  const all = cats || [];
  const picked = all.filter((c) => c.show_in_menu).sort((a, b) => (a.menu_order ?? 999) - (b.menu_order ?? 999) || a.name.localeCompare(b.name));
  return picked.length ? picked : all.filter((c) => !c.parent_id);
}

/** Desktop "Shop" dropdown — a simple list: All Products, then the menu categories. */
function ShopMenu({ items }) {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();
  const ref = React.useRef(null);
  const timer = React.useRef(null);
  React.useEffect(() => { setOpen(false); }, [pathname]);
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDown); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const hover = () => window.matchMedia("(hover: hover)").matches;
  const enter = () => { if (!hover()) return; clearTimeout(timer.current); setOpen(true); };
  const leave = () => { if (!hover()) return; timer.current = setTimeout(() => setOpen(false), 150); };
  const active = pathname.startsWith("/shop") || pathname.startsWith("/product/");
  const item = ({ isActive }) => `flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-[14px] transition-colors ${isActive ? "bg-[#E8F3EE] text-[#0E4D34] font-semibold" : "text-neutral-700 hover:bg-neutral-50 hover:text-[#0E4D34]"}`;

  return (
    <div className="relative" ref={ref} onMouseEnter={enter} onMouseLeave={leave}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} data-testid="nav-link-shop"
              className={`flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold whitespace-nowrap transition-colors ${active || open ? "text-[#991B1B]" : "text-neutral-700 hover:text-[#0E4D34]"}`}>
        Shop<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div key="shopmenu" role="menu" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }}
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-5 z-50" data-testid="shop-menu">
            <div className="w-72 bg-white rounded-2xl shadow-2xl border border-neutral-200 p-2.5">
              <NavLink to="/shop" end className={({ isActive }) => `flex items-center justify-between rounded-xl px-3 py-3 text-[14px] font-semibold ${isActive ? "bg-[#0E4D34] text-white" : "bg-[#0E4D34] text-white hover:bg-[#0A3926]"}`} data-testid="shop-menu-all">
                All Products<ArrowRight size={15}/>
              </NavLink>
              <div className="my-2 h-px bg-neutral-100"/>
              {items.map((c) => (
                <NavLink key={c.id} to={`/shop/${c.slug}`} end className={item} data-testid={`shop-menu-${c.slug}`}>
                  <span>{c.name}</span><ChevronRight size={14} className="text-neutral-300"/>
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header() {
  const { user, cart, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);
  const navigate = useNavigate();

  // "Shop" is a dropdown built from Admin → Categories (main category → its sub-categories)
  const links = [
    { to: "/blog", label: "Blog" },
    { to: "/lookbook", label: "Lookbook" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data), staleTime: 60 * 1000 });
  const menuItems = React.useMemo(() => menuCategories(cats), [cats]);
  const [shopOpenMobile, setShopOpenMobile] = React.useState(true);

  const location = useLocation();
  const [acctOpen, setAcctOpen] = React.useState(false);
  const acctRef = React.useRef(null);

  // Close menus on navigation; lock page scroll while the drawer is open
  React.useEffect(() => { setOpen(false); setAcctOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  React.useEffect(() => {
    if (!acctOpen) return;
    const close = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [acctOpen]);

  const iconBtn = "p-2 rounded-full hover:bg-neutral-100 transition-colors";

  return (
    <>
      <AnnouncementBar/>
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-neutral-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between gap-4 h-16 sm:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <Logo size={44} wordmark={false} className="sm:hidden"/>
            <Logo size={52} className="hidden sm:inline-flex"/>
          </Link>

          {/* Full menu on laptops/desktops (≥1024px). Tablets/phones use the drawer. */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            <ShopMenu items={menuItems}/>
            {links.map((l) => (
              <NavLink
                key={l.to} to={l.to} end
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) => `text-xs tracking-[0.2em] uppercase font-semibold whitespace-nowrap transition-colors ${isActive ? "text-[#991B1B]" : "text-neutral-700 hover:text-[#0E4D34]"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-0.5 sm:gap-1.5">
            <button onClick={() => navigate("/shop?focus=search")} aria-label="Search" data-testid="btn-search" className={`${iconBtn} hidden sm:inline-flex`}><Search size={19}/></button>
            <Link to={user ? "/account/wishlist" : "/login?redirect=/account/wishlist"} aria-label="Wishlist" data-testid="btn-wishlist" className={`${iconBtn} hover:text-[#991B1B]`}><Heart size={19}/></Link>
            {user ? (
              <div className="relative hidden sm:block" ref={acctRef}
                   onMouseEnter={() => window.matchMedia("(hover: hover)").matches && setAcctOpen(true)}
                   onMouseLeave={() => window.matchMedia("(hover: hover)").matches && setAcctOpen(false)}>
                <button onClick={() => setAcctOpen((v) => !v)} aria-label="Account" data-testid="btn-account" className={iconBtn}><User size={19}/></button>
                {acctOpen && (
                  <div className="absolute right-0 top-full pt-2 w-56">
                    <div className="bg-white shadow-xl border border-neutral-200 rounded-xl py-2 overflow-hidden">
                      <div className="px-4 py-2 text-xs text-neutral-500">Signed in as</div>
                      <div className="px-4 pb-2 text-sm font-semibold truncate">{user.email}</div>
                      <hr/>
                      <Link to="/account" className="block px-4 py-2 text-sm hover:bg-neutral-50" data-testid="menu-account">My Account</Link>
                      <Link to="/account/orders" className="block px-4 py-2 text-sm hover:bg-neutral-50" data-testid="menu-orders">Orders</Link>
                      {user.role !== "customer" && (
                        <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-neutral-50 text-[#0E4D34] font-semibold" data-testid="menu-admin">Admin Panel</Link>
                      )}
                      <button onClick={logout} className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 text-[#991B1B]" data-testid="menu-logout">Sign Out</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" aria-label="Sign in" data-testid="btn-login" className={`${iconBtn} hidden sm:inline-flex`}><User size={19}/></Link>
            )}
            <Link to="/cart" aria-label="Bag" data-testid="btn-cart" className={`${iconBtn} hover:text-[#991B1B] relative`}>
              <ShoppingBag size={19}/>
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-[#991B1B] text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center font-bold">{cartCount}</span>
              )}
            </Link>
            {/* Menu button sits at the far RIGHT on phones & tablets */}
            <button className={`${iconBtn} lg:hidden ml-1`} onClick={() => setOpen(true)} aria-label="Open menu" data-testid="menu-toggle">
              <Menu size={22}/>
            </button>
          </div>
        </div>
      </header>

      {/* Slide-in drawer from the right */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div key="scrim" className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setOpen(false)}/>
            <motion.aside key="drawer" data-testid="mobile-menu"
                          className="fixed top-0 right-0 bottom-0 z-[61] w-[86%] max-w-sm bg-white shadow-2xl flex flex-col lg:hidden"
                          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.28 }}>
              <div className="flex items-center justify-between px-5 h-16 border-b">
                <Logo size={38} subtitle="Collections"/>
                <button onClick={() => setOpen(false)} aria-label="Close menu" className={iconBtn} data-testid="menu-close"><X size={22}/></button>
              </div>
              <form className="px-5 pt-4" onSubmit={(e) => { e.preventDefault(); const q = new FormData(e.currentTarget).get("q"); navigate(`/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); }}>
                <div className="flex items-center gap-2 border border-neutral-300 rounded-full px-4 py-2.5 focus-within:border-[#0E4D34]">
                  <Search size={16} className="text-neutral-500"/>
                  <input name="q" placeholder="Search sarees, kurtis…" className="flex-1 text-sm outline-none bg-transparent"/>
                </div>
              </form>
              <nav className="flex-1 overflow-y-auto px-5 py-3">
                <div className="border-b border-neutral-100">
                  <button onClick={() => setShopOpenMobile((v) => !v)} aria-expanded={shopOpenMobile} data-testid="drawer-shop-toggle"
                          className="w-full flex items-center justify-between py-3.5 text-sm uppercase tracking-[0.2em] text-neutral-800">
                    Shop<ChevronDown size={16} className={`text-neutral-400 transition-transform ${shopOpenMobile ? "rotate-180" : ""}`}/>
                  </button>
                  {shopOpenMobile && (
                    <div className="pb-3 space-y-0.5" data-testid="drawer-shop-menu">
                      <NavLink to="/shop" end className={({ isActive }) => `block rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-[#E8F3EE] text-[#0E4D34]" : "text-neutral-900 hover:bg-neutral-50"}`}>All Products</NavLink>
                      {menuItems.map((c) => (
                        <NavLink key={c.id} to={`/shop/${c.slug}`} end className={({ isActive }) => `block rounded-lg pl-6 pr-3 py-2 text-sm ${isActive ? "bg-[#E8F3EE] text-[#0E4D34] font-semibold" : "text-neutral-700 hover:bg-neutral-50"}`}>{c.name}</NavLink>
                      ))}
                    </div>
                  )}
                </div>
                {links.map((l) => (
                  <NavLink key={l.to} to={l.to} end
                           className={({ isActive }) => `flex items-center justify-between py-3.5 text-sm uppercase tracking-[0.2em] border-b border-neutral-100 ${isActive ? "text-[#991B1B] font-semibold" : "text-neutral-800"}`}>
                    {l.label}<ChevronRight size={16} className="text-neutral-400"/>
                  </NavLink>
                ))}
              </nav>
              <div className="border-t px-5 py-4 space-y-2 bg-[#FAF9F6]">
                {user ? (
                  <>
                    <div className="text-xs text-neutral-500 truncate">Signed in as <span className="font-semibold text-neutral-800">{user.email}</span></div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/account" className="text-center text-xs uppercase tracking-widest border rounded-lg py-2.5 bg-white">My Account</Link>
                      <Link to="/account/orders" className="text-center text-xs uppercase tracking-widest border rounded-lg py-2.5 bg-white">Orders</Link>
                    </div>
                    {user.role !== "customer" && <Link to="/admin" className="block text-center text-xs uppercase tracking-widest rounded-lg py-2.5 bg-[#0E4D34] text-white">Admin Panel</Link>}
                    <button onClick={() => { setOpen(false); logout(); }} className="w-full text-center text-xs uppercase tracking-widest text-[#991B1B] py-2">Sign Out</button>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/login" className="text-center text-xs uppercase tracking-widest rounded-lg py-3 bg-[#0E4D34] text-white" data-testid="drawer-login">Sign In</Link>
                    <Link to="/register" className="text-center text-xs uppercase tracking-widest border border-[#0E4D34] text-[#0E4D34] rounded-lg py-3 bg-white">Register</Link>
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export function Footer() {
  const site = useSiteSettings();
  const footerCms = useCms("footer");
  return (
    <footer className="bg-[#0E4D34] text-neutral-100 mt-16 sm:mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12 sm:py-16 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
        <div className="col-span-2 lg:col-span-1">
          <Logo size={60} light className="mb-5"/>
          <p className="text-sm opacity-80 leading-relaxed">{footerCms.tagline}</p>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-white/80 mb-3">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop/sarees" className="hover:text-white/70">Sarees</Link></li>
            <li><Link to="/shop/kurtis" className="hover:text-white/70">Kurtis</Link></li>
            <li><Link to="/shop/custom" className="hover:text-white/70">Custom Designs</Link></li>
            <li><Link to="/lookbook" className="hover:text-white/70">Lookbook</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-white/80 mb-3">Care</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white/70">About Us</Link></li>
            <li><Link to="/blog" className="hover:text-white/70">Blog</Link></li>
            <li><Link to="/journey" className="hover:text-white/70">Our Journey</Link></li>
            <li><Link to="/contact" className="hover:text-white/70">Contact</Link></li>
            <li><Link to="/account/orders" className="hover:text-white/70">Track Order</Link></li>
          </ul>
        </div>
        <div className="col-span-2 lg:col-span-1">
          <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-white/80 mb-3">Connect</h4>
          <div className="flex gap-3 mb-4">
            {site.instagram && <a href={site.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2.5 rounded-full border border-white/30 hover:bg-white/10"><Instagram size={16}/></a>}
            {site.facebook && <a href={site.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2.5 rounded-full border border-white/30 hover:bg-white/10"><Facebook size={16}/></a>}
            {site.youtube && <a href={site.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="p-2.5 rounded-full border border-white/30 hover:bg-white/10"><Youtube size={16}/></a>}
            <a href={`mailto:${site.email}`} aria-label="Email" className="p-2.5 rounded-full border border-white/30 hover:bg-white/10"><Mail size={16}/></a>
          </div>
          <p className="text-xs opacity-80 leading-relaxed whitespace-pre-line">{site.address}{site.address && site.phone ? "\n" : ""}{site.phone && <a href={telHref(site.phone)} className="hover:underline">{site.phone}</a>}</p>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 px-4 text-center text-xs flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3">
        <span className="opacity-70">{footerCms.copyright}</span>
        <span className="hidden sm:inline opacity-40">|</span>
        <span className="opacity-80">Developed by{" "}
          <a href="https://x7ittechnologies.com" target="_blank" rel="noopener noreferrer" data-testid="footer-developer"
             className="font-semibold text-white underline-offset-4 hover:text-[#4DA3FF] hover:underline transition-colors">X7 IT Technologies</a>
        </span>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header/>
      <main className="flex-1">{children}</main>
      <Footer/>
      <WhatsAppWidget/>
    </div>
  );
}
